import { type FC, type MutableRefObject, useState, useCallback, useEffect } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { CLEAR_EDITOR_COMMAND, type TextNode } from 'lexical';
import { useSelector } from 'react-redux';

import { CopilotTrackEvent } from 'constants/copilot-event';
import { EventPlace } from 'helpers/analytics';
import { useCompleteOnboardingStep } from 'hooks/api/onboarding/use-complete-onboarding-step';
import { usePrevious } from 'hooks/use-previous';
import { type Suggestion } from 'interfaces/copilot';
import { ProductOnboardingStep } from 'interfaces/entities/product-onboarding';
import { trackEvent } from 'services/event-tracking';
import { getIsCopilotModalExpanded, getCopilotIsLoading } from 'store/views/copilot/selectors';

import { CommandMenu } from '../picker/CommandMenu';
import { type CopilotPickerOption } from '../picker/copilot-picker-option';
import { EmptyState } from '../picker/EmptyState';

import { useCustomTypeahead } from './hooks/use-custom-typeahead';
import { useDynamicPickerOptions } from './hooks/use-dynamic-options';

interface IProps {
  suggestions: Required<Suggestion>[];
  isCommandMenuVisibleRef: MutableRefObject<boolean>;
}

export const CommandMenuPlugin: FC<IProps> = ({ suggestions, isCommandMenuVisibleRef }) => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);

  const { checkTriggerMatch } = useCustomTypeahead();
  const { options } = useDynamicPickerOptions({ suggestions, queryString });
  const isLoading = useSelector(getCopilotIsLoading);
  const isExpanded = useSelector(getIsCopilotModalExpanded);
  const prevIsExpanded = usePrevious(isExpanded);
  const { completeStep } = useCompleteOnboardingStep();

  useEffect(() => {
    const shouldClearEditor = isCommandMenuVisibleRef.current && isExpanded !== prevIsExpanded;

    if (shouldClearEditor) {
      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
    }
  }, [isExpanded, prevIsExpanded, editor]);

  // eslint-disable-next-line react-compiler/react-compiler
  isCommandMenuVisibleRef.current = !!options.length;

  const clearEditor = useCallback(() => {
    editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
  }, [editor]);

  const onSelectOption = useCallback(
    (selectedOption: CopilotPickerOption, nodeToRemove: TextNode | null, closeMenu: () => void) => {
      if (isLoading) {
        return;
      }

      clearEditor();
      editor.update(() => {
        nodeToRemove?.remove();
        selectedOption.onSelect();
        closeMenu();
      });

      trackEvent(CopilotTrackEvent.MessageSent, EventPlace.One, {
        source: 'command popover',
        state: isExpanded ? 'fullscreen' : 'popover',
      });
      completeStep({ step: ProductOnboardingStep.CopilotChat });
    },
    [editor, isLoading, isExpanded, clearEditor, completeStep],
  );

  return (
    <LexicalTypeaheadMenuPlugin<CopilotPickerOption>
      onQueryChange={setQueryString}
      triggerFn={checkTriggerMatch}
      onSelectOption={onSelectOption}
      options={options}
      onClose={() => (isCommandMenuVisibleRef.current = false)}
      menuRenderFn={(_, { selectOptionAndCleanUp, setHighlightedIndex, selectedIndex }) => {
        if (isLoading) {
          return <EmptyState />;
        }

        return options.length ? (
          <CommandMenu
            options={options}
            selectOption={selectOptionAndCleanUp}
            selectedIndex={selectedIndex}
            setHighlightedIndex={setHighlightedIndex}
            clearEditor={clearEditor}
          />
        ) : null;
      }}
    />
  );
};
